import { Button, Divider, Form, message, Typography } from "antd";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useParams } from "react-router-dom";
import { IFinanceTableFormProps } from "../../../../../interfaces/props/IFinanceTableFormProps";
import { useGetApplicationDataToBankQuery } from "../../../../../store/api/applicationBankApi/applicationBank.api";
import { useLazyGetAccountingBalanceDataQuery } from "../../../../../store/api/applicationBankApi/finance.api";
import { useAppSelector } from "../../../../../store/reduxHooks";
import {
  SELECT_APPLICATIONS_DATA_TO_BANK_ACCOUNTING_BALANCE,
  SELECT_APPLICATIONS_DATA_TO_BANK_BALANCE,
} from "../../../../../store/selectors/applicationDataToBankSelector";
import {
  APPLICATION_BANK_STATUS_KKB,
  BANK_NAME,
} from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { Guard } from "../../../../../utils/guards/Guard";
import { formatCurrency } from "../../../../../utils/utilities/CurrencyFormatter";
import { FinanceTable } from "./FinanceTable/FinanceTable";

import classes from "./FinanceTableForm.module.css";

const { Text } = Typography;

/**
 * @description через form.getField(s) приходит такой массив. Каждый элемент в нем - строка
 */
type TableRowlistItemType = [
  code: string,
  fields: {
    [key: string]: number;
  },
];

export function FinanceTableForm({
  form,
}: IFinanceTableFormProps): ReactElement {
  // Данные для построения таблицы "Бухгалтерский баланс"
  const { applicationId, applicationBankId } = useParams();
  const { data: applicationBankData } = useGetApplicationDataToBankQuery({
    applicationId: Number(applicationId),
    applicationBankId: Number(applicationBankId),
  });
  const accountingBalanceTableData = useAppSelector(
    SELECT_APPLICATIONS_DATA_TO_BANK_ACCOUNTING_BALANCE,
  );
  const balanceData = useAppSelector(SELECT_APPLICATIONS_DATA_TO_BANK_BALANCE);
  const [trigger, { isLoading: isAccountingDataLoading }] =
    useLazyGetAccountingBalanceDataQuery();

  if (!balanceData || !accountingBalanceTableData) return <></>;

  // Чекбоксы "Виды налогообложения"
  const {
    osn,
    usn_income,
    usn_income_expenses,
    full_form_balance_opiu,
    simplified_form,
    patent,
    envd,
  } = balanceData;

  const { data, balance_date_lc } = accountingBalanceTableData;

  const { active, passive, report } = data;
  const initialValues = {
    accountingBalance: {
      osn,
      usn_income,
      usn_income_expenses,
      full_form_balance_opiu,
      simplified_form,
      patent,
      envd,
      data,
      balance_date_lc: balance_date_lc
        ? dayjs(balance_date_lc).get("year")
        : dayjs().subtract(1, "year").get("year"),
      balance_date_rc: balance_date_lc
        ? dayjs(balance_date_lc).get("year")
        : dayjs().subtract(1, "year").get("year"),
    },
  };

  /**
   * Пересчёт значения в суммирующих строках при изменении значений в таблице
   * (в колонках, где код начинается с 1)
   *
   * При изменении строки находим соответствующую суммирующую строку (по коду - первые две цифры - раздел
   * Если последние две - 00 - строка суммирующая) и пересчитываем значение в ней.
   *
   * @param {string} inputSide - Сторона, на которой произошло изменение (lc или rc)
   */
  function balanceRecalculation(inputSide: string) {
    const tableValues: TableRowlistItemType[] = Object.entries(
      form.getFieldsValue().accountingBalance.data,
    );

    const filteredTableValues = tableValues.filter((row) => {
      const rowCode = row[0];
      rowCode.startsWith("1") && rowCode.endsWith("00");
    });

    const balanceOneRows = ["1100", "1200"];
    const balanceTwoRows = ["1300", "1400", "1500"];
    let balanceTypeOneValue = 0;
    let balanceTypeTwoValue = 0;

    balanceOneRows.forEach((rowCode) => {
      const value = filteredTableValues.find((row) => row[0] === rowCode);
      if (value) balanceTypeOneValue += value[1][inputSide];
    });
    balanceTwoRows.forEach((rowCode) => {
      const value = filteredTableValues.find((row) => row[0] === rowCode);
      if (value) balanceTypeTwoValue += value[1][inputSide];
    });
    form.setFieldValue(
      ["accountingBalance", "data", "1600", inputSide],
      formatCurrency(balanceTypeOneValue),
    );
    form.setFieldValue(
      ["accountingBalance", "data", "1700", inputSide],
      formatCurrency(balanceTypeTwoValue),
    );
  }

  const summarizeRows = (
    summarizeRow: TableRowlistItemType[],
    tableValues: TableRowlistItemType[],
    inputSide: string,
  ) => {
    const [summarizeRowCode] = summarizeRow[0]; // достаем первую и единственную строку в массиве.
    let newValue = 0;
    // Находим нужные строки и суммируем значения их полей
    tableValues.forEach((tableValue) => {
      const [tableValueCode, tableValueFields] = tableValue;
      if (
        tableValueCode?.slice(0, 2) === summarizeRowCode.slice(0, 2) && //Проверяем, что строка из нужного отдела и не суммирующая
        !tableValueCode?.endsWith("00")
      ) {
        if (inputSide === "lc") newValue += tableValueFields.lc;
        if (inputSide === "rc") newValue += tableValueFields.rc;
      }
    });

    form.setFieldValue(
      ["accountingBalance", "data", summarizeRowCode, inputSide],
      formatCurrency(newValue),
    );

    balanceRecalculation(inputSide);
  };

  /**
   * Суммирование значений строк в третьей таблице и обновления значений в форме.
   *
   * @param tableValues - массив значений строк таблицы
   * @param inputSide - сторона ввода ('lc' или 'rc')
   */
  function thirdTableSummarizeRows(
    tableValues: TableRowlistItemType[],
    inputSide: string,
  ) {
    const filteredTableValues = tableValues.filter(
      (row) => row[0].startsWith("2") && !row[0].endsWith("00"),
    );

    const rowsToSummarize = tableValues.filter(
      (row) => row[0].startsWith("2") && row[0].endsWith("00"),
    );

    rowsToSummarize.forEach((sumRow) => {
      const [sumRowCode] = sumRow;
      let sumValue = 0;
      filteredTableValues.forEach((row) => {
        const [rowCode, rowFields] = row;
        if (
          Number(rowCode?.slice(1, 2)) <= Number(sumRowCode?.slice(1, 2)) &&
          !rowCode.endsWith("11") &&
          !rowCode.endsWith("12")
        ) {
          if (inputSide === "lc") sumValue += rowFields.lc;
          if (inputSide === "rc") sumValue += rowFields.rc;
        }
      });
      form.setFieldValue(
        ["accountingBalance", "data", sumRowCode, inputSide],
        formatCurrency(sumValue),
      );
    });
  }

  /**
   * Обработчик изменения значения в таблице.
   * Вызывается при изменении значения в строке таблицы.
   *
   * @param changedRow - измененная строка таблицы
   * @param tableValues - текущие значения таблицы
   */
  function handleValueChange(
    changedRow: TableRowlistItemType[],
    tableValues: TableRowlistItemType[],
  ) {
    const inputSide = Object.keys(changedRow[0][1])[0];
    const sumRow = tableValues?.find((row) => {
      return (
        row[0]?.slice(0, 2) === changedRow[0][0]?.slice(0, 2) &&
        row[0]?.endsWith("00")
      );
    });

    const summarizeRow: TableRowlistItemType[] = [];
    if (sumRow) summarizeRow.push(sumRow);
    if (summarizeRow[0][0]?.startsWith("2"))
      thirdTableSummarizeRows(tableValues, inputSide);
    else summarizeRows(summarizeRow, tableValues, inputSide);
  }

  /**
   * Подтягивает значения БО для полей таблицы по году, который выбран в селекторе.
   * Значения, которые подтягиваются таким образом, делятся на 1000,
   * так как в таблице необходимо указывать значения в тысячах ₽.
   */
  function handleGetAccountingData() {
    let date = form.getFieldValue(["accountingBalance", "balance_date_rc"]);
    if (typeof date === "string") {
      date = Number(date.slice(0, 4));
    }

    trigger({
      year: date,
      applicationId: Number(applicationId),
      applicationBankId: Number(applicationBankId),
    }).then((response) => {
      if (response.error) {
        const errorMessage = response.error?.data?.message;
        message.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Не получилось загрузить данные",
        );
        return;
      }
      if (response.isSuccess) {
        /*
         * accountingBalanceData может приходить undefined если с таблицей ничего не делали и сразу нажали подтянуть данные
         * В таком случае balanceData принимает значение ответа
         */

        const balanceData = response.data;
        form.setFieldsValue(balanceData);
        if (balanceData) {
          const dataArray = Object.entries(balanceData);

          dataArray.splice(0, 3);

          // из всего обьекта достаем только строки через якорь c_
          const filteredData = dataArray.filter(([rowCode]) =>
            rowCode.includes("c_"),
          );

          filteredData.forEach(([dataRowCode, dataRowValue]) => {
            form.setFieldValue(
              [
                "accountingBalance",
                "data",
                dataRowCode?.replace("c_", ""),
                "lc",
              ],
              Number(dataRowValue) / 1000,
            );
          });

          const tableValues =
            form.getFieldsValue().accountingBalance.data || [];

          filteredData.forEach((dataRow) => {
            const tableRowTemplate: TableRowlistItemType[] = [];
            tableRowTemplate.push([
              dataRow[0].replace("c_", ""),
              {
                lc: Number(dataRow[1]),
              },
            ]);

            if (tableValues) {
              handleValueChange(tableRowTemplate, Object.entries(tableValues));
            }
            // Переделать
          });
          message.success(`Загружены данные за ${date} год`);
        }
      }
    });
  }

  return (
    <>
      {accountingBalanceTableData && (
        <Form
          form={form}
          autoComplete="off"
          initialValues={initialValues}
          onValuesChange={(item, values) => {
            // если поле таблицы - инпут
            if (item?.accountingBalance?.data) {
              const sectionCode = Object.entries(
                item?.accountingBalance?.data,
              )[0][0].slice(0, 2);

              if (sectionCode !== "16" && sectionCode !== "17")
                handleValueChange(
                  Object.entries(item.accountingBalance?.data),
                  Object.entries(values.accountingBalance?.data),
                );
            }
          }}
        >
          <Divider
            orientation="left"
            orientationMargin="0"
            className={classes.financeTableDivider}
          >
            Бухгалтерский баланс
          </Divider>

          {!!applicationBankData && (
            <Guard.ApplicationBankStatus
              status={applicationBankData?.status}
              allowedStatuses={[
                {
                  bank_name: BANK_NAME.KKB,
                  type: APPLICATION_BANK_STATUS_KKB.PROCESSING,
                },
                {
                  bank_name: BANK_NAME.KKB,
                  type: APPLICATION_BANK_STATUS_KKB.REVISION,
                },
              ]}
            >
              <div className={classes.getDataFromOutsideButton}>
                <Button
                  loading={isAccountingDataLoading}
                  className={classes.getDataFromOutsideButton}
                  onClick={() => handleGetAccountingData()}
                >
                  Загрузить данные из внешних источников
                </Button>
              </div>
            </Guard.ApplicationBankStatus>
          )}

          <div className={classes.tableContainer}>
            {active && (
              <div>
                <Text className={classes.tableName} type="secondary">
                  Актив
                </Text>

                <FinanceTable
                  dataSource={active}
                  codes={active.map((item) => item.code)}
                />
              </div>
            )}

            {passive && (
              <div>
                <Text className={classes.tableName} type="secondary">
                  Пассив
                </Text>
                <FinanceTable
                  dataSource={passive}
                  codes={passive.map((item) => item.code)}
                />
              </div>
            )}

            {report && (
              <div>
                <Text className={classes.tableName} type="secondary">
                  Отчет о финансовых результатах
                </Text>
                <FinanceTable
                  dataSource={report}
                  codes={report.map((item) => item.code)}
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </>
  );
}
